import { __decorate } from "tslib";
import { Sim } from '@dooboostore/simple-boot';
export var TossService;
(function (TossService) {
    TossService.SYMBOL = Symbol.for('TossService');
})(TossService || (TossService = {}));
// ── Search AutoComplete ────────────────────────────────────────────
export const TOSS_SEARCH_SECTION_TYPES = ['SCREENER', 'NEWS', 'PRODUCT', 'TICS', 'MARKET_INDEX', 'COMPANY_REPORT', 'ETF', 'RELATED_TOPIC', 'COMPANY_TICS', 'PRODUCT_DETAIL'];
export default (container) => {
    let TossServiceImpl = class TossServiceImpl {
        constructor() {
            // allorigins는 서버/브라우저 모두 동작 (간헐적 522 타임아웃 발생)
            Object.defineProperty(this, "CORS_PROXY", {
                enumerable: true,
                configurable: true,
                writable: true,
                value: 'https://sparkling-dew-b13c.visualkhh.workers.dev/?url='
            });
            Object.defineProperty(this, "CHART_BASE", {
                enumerable: true,
                configurable: true,
                writable: true,
                value: 'https://wts-info-api.tossinvest.com/api/v1/c-chart'
            });
            Object.defineProperty(this, "SEARCH_BASE", {
                enumerable: true,
                configurable: true,
                writable: true,
                value: 'https://wts-info-api.tossinvest.com/api/v3/search-all/wts-auto-complete'
            });
            Object.defineProperty(this, "STOCK_INFO_BASE", {
                enumerable: true,
                configurable: true,
                writable: true,
                value: 'https://wts-info-api.tossinvest.com/api/v2/stock-infos'
            });
            Object.defineProperty(this, "STOCK_DETAIL_BASE", {
                enumerable: true,
                configurable: true,
                writable: true,
                value: 'https://wts-info-api.tossinvest.com/api/v1/stock-detail/ui/wts'
            });
            Object.defineProperty(this, "DASHBOARD_BASE", {
                enumerable: true,
                configurable: true,
                writable: true,
                value: 'https://wts-info-api.tossinvest.com/api/v2/dashboard/wts'
            });
        }
        async fetchJson(url, init) {
            // 정적 배포 전제: 전부 workers 프록시 경유 (dev 포함)
            const proxied = `${this.CORS_PROXY}${encodeURIComponent(url)}`;
            const res = await fetch(proxied, init);
            if (!res.ok)
                throw new Error(`HTTP ${res.status}`);
            return (await res.json());
        }
        async getChart(target, options = {}) {
            const { count = 61, timeframe = 'day:1', session = 'all', investMode = 'integrated', useAdjustedRate = true, from, } = options;
            // target 정규화: 문자열 코드 또는 회사 정보 객체
            let code;
            let marketHint;
            if (typeof target === 'string') {
                code = target;
            }
            else if ('productCode' in target) {
                code = target.productCode;
                marketHint = target.market;
            }
            else {
                code = target.company.code;
                marketHint = target.market.code;
            }
            // code 정규화: 영문 시작(A/US/KGG 등) 그대로, 숫자 6자리 → A prefix
            const normalizedCode = /^[A-Z]/.test(code) ? code : `A${code}`;
            // 회사 정보가 있으면 market으로 해외 여부 판단 (NSQ/NYS/NAS/AMX), 없으면 코드 prefix로 판단 (US/NAS/AMX/NYS)
            const isUS = /^(US|NAS|AMX|NYS)/.test(normalizedCode) ||
                (marketHint ? /^(NSQ|NYS|NAS|AMX)/.test(marketHint) : false);
            const marketPath = isUS ? 'us-s' : 'kr-s';
            const params = new URLSearchParams({
                count: String(count),
                session,
                investMode,
                useAdjustedRate: String(useAdjustedRate),
                ...(from ? { from } : {}),
            });
            // week/month/min은 session 파라미터 불필요 시 제외 (서버가 무시하지만 URL 정리)
            const isIntradayOrWeekly = timeframe.startsWith('min:') || timeframe.startsWith('week:') || timeframe.startsWith('month:');
            if (isIntradayOrWeekly && params.has('session')) {
                // 분/주/월은 session=all이 무의미할 수 있으나 기존 호출 호환을 위해 유지
            }
            const url = `${this.CHART_BASE}/${marketPath}/${normalizedCode}/${timeframe}?${params.toString()}`;
            const json = await this.fetchJson(url, {
                headers: { accept: 'application/json' },
            });
            if (!json.result)
                throw new Error('Invalid chart response');
            return json.result;
        }
        async searchAutoComplete(query, sections = [
            { type: 'SCREENER' },
            { type: 'NEWS' },
            { type: 'PRODUCT', option: { addIntegratedSearchResult: true } },
            { type: 'TICS' },
            { type: 'MARKET_INDEX' },
        ]) {
            const body = { query, sections: [...sections] };
            const json = await this.fetchJson(this.SEARCH_BASE, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                },
                body: JSON.stringify(body),
            });
            return json.result ?? [];
        }
        async searchProduct(query) {
            const results = await this.searchAutoComplete(query, [
                { type: 'PRODUCT', option: { addIntegratedSearchResult: true } },
            ]);
            const productSection = results.find((r) => r.type === 'PRODUCT');
            return productSection?.data.items ?? [];
        }
        async searchMarketIndex(query) {
            const results = await this.searchAutoComplete(query, [{ type: 'MARKET_INDEX' }]);
            const section = results.find((r) => r.type === 'MARKET_INDEX');
            return section?.data.items ?? [];
        }
        async getStability(code) {
            const normalizedCode = code.startsWith('A') ? code : `A${code}`;
            const url = `${this.STOCK_INFO_BASE}/stability/${normalizedCode}`;
            const json = await this.fetchJson(url, {
                method: 'POST',
                headers: { accept: 'application/json' },
            });
            if (!json.result)
                throw new Error('Invalid stability response');
            return json.result;
        }
        async getInvestmentIndicators(code) {
            const normalizedCode = code.startsWith('A') ? code : `A${code}`;
            const url = `${this.STOCK_DETAIL_BASE}/${normalizedCode}/investment-indicators`;
            const json = await this.fetchJson(url, {
                headers: { accept: 'application/json' },
            });
            const sections = json.result?.indicatorSections ?? [];
            const find = (name) => {
                const sec = sections.find(s => s.sectionName === name);
                if (!sec)
                    throw new Error(`Missing section: ${name}`);
                return sec.data;
            };
            return {
                valuation: find('가치평가'),
                profit: find('수익'),
                dividend: find('배당'),
            };
        }
        async getValuation(code) {
            return (await this.getInvestmentIndicators(code)).valuation;
        }
        async getProfit(code) {
            return (await this.getInvestmentIndicators(code)).profit;
        }
        async getDividend(code) {
            return (await this.getInvestmentIndicators(code)).dividend;
        }
        async getOverview(code) {
            const normalizedCode = code.startsWith('A') ? code : `A${code}`;
            const url = `https://wts-info-api.tossinvest.com/api/v2/stock-infos/${normalizedCode}/overview`;
            const json = await this.fetchJson(url, {
                headers: { accept: 'application/json' },
            });
            if (!json.result)
                throw new Error('Invalid overview response');
            return json.result;
        }
        async getProductDetail(productOrCode, companyCode, query) {
            let productCode;
            let cCode;
            let q;
            if (typeof productOrCode === 'string') {
                productCode = productOrCode;
                cCode = companyCode;
                q = query ?? productCode;
            }
            else {
                productCode = productOrCode.productCode;
                cCode = productOrCode.companyCode;
                q = productOrCode.productName;
            }
            const results = await this.searchAutoComplete(q, [
                { type: 'COMPANY_REPORT', option: { companyCode: cCode } },
                { type: 'ETF' },
                { type: 'RELATED_TOPIC', option: { productCode } },
                { type: 'COMPANY_TICS', option: { companyCode: cCode } },
                { type: 'PRODUCT_DETAIL', option: { productCode } },
            ]);
            const sec = results.find((r) => r.type === 'PRODUCT_DETAIL');
            return sec?.data ?? null;
        }
        normalizeTradeCode(code) {
            return /^[A-Z]/.test(code) ? code : `A${code}`;
        }
        async fetchTradeBody(path, code, size, paramName) {
            const normalizedCode = this.normalizeTradeCode(code);
            const url = `https://wts-info-api.tossinvest.com/api/v1/${path}?${paramName}=${normalizedCode}&size=${size}`;
            const json = await this.fetchJson(url, {
                headers: { accept: 'application/json' },
            });
            return json.result?.body ?? [];
        }
        async getTradingTrend(code, size = 60) {
            return this.fetchTradeBody('stock-infos/trade/trend/trading-trend', code, size, 'productCode');
        }
        async getProgramTrading(code, size = 50) {
            return this.fetchTradeBody('stock-infos/trade/trend/program-trading', code, size, 'productCode');
        }
        async getMarginLoan(code, size = 50) {
            return this.fetchTradeBody('mds/info/margin-loan', code, size, 'stockCode');
        }
        async getLendingTrading(code, size = 50) {
            return this.fetchTradeBody('mds/info/lending-trading', code, size, 'stockCode');
        }
        async getShortSellingTrend(code, size = 50) {
            return this.fetchTradeBody('mds/info/short-selling-trend', code, size, 'stockCode');
        }
        async getCFD(code, size = 50) {
            return this.fetchTradeBody('mds/info/cfd', code, size, 'stockCode');
        }
        async getTicsRanking(params) {
            const url = `${this.DASHBOARD_BASE}/overview/tics/ranking`;
            const json = await this.fetchJson(url, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                },
                body: JSON.stringify(params),
            });
            if (!json.result)
                throw new Error('Invalid TICS ranking response');
            return json.result;
        }
        async getTicsRankingKrByAmount(duration = '1d') {
            return this.getTicsRanking({ nation: 'KR', duration, sortBy: 'TRADING_AMOUNT' });
        }
        async getTicsRankingKrByFluctuation(duration = '1d') {
            return this.getTicsRanking({ nation: 'KR', duration, sortBy: 'FLUCTUATION_RATE' });
        }
        async getTicsRankingUsByAmount(duration = '1d') {
            return this.getTicsRanking({ nation: 'US', duration, sortBy: 'TRADING_AMOUNT' });
        }
        async getTicsRankingUsByFluctuation(duration = '1d') {
            return this.getTicsRanking({ nation: 'US', duration, sortBy: 'FLUCTUATION_RATE' });
        }
        async getTicsComparisonChart(req) {
            const defaultIndicator = req.nation === 'US' ? 'SPX.CBI' : 'KGG01P';
            const { ticsId, nation, securitiesType = 'STOCK', indicatorCode = defaultIndicator } = req;
            const url = `https://wts-info-api.tossinvest.com/api/v1/dashboard/wts/overview/tics/${ticsId}/comparison-chart?nation=${encodeURIComponent(nation)}&securitiesType=${encodeURIComponent(securitiesType)}&indicatorCode=${encodeURIComponent(indicatorCode)}`;
            const json = await this.fetchJson(url, {
                headers: { accept: 'application/json' },
            });
            if (!json.result)
                throw new Error('Invalid TICS comparison chart response');
            return json.result;
        }
        async getStockPrices(productCodes) {
            if (!productCodes.length)
                return [];
            const codes = productCodes.map(c => /^[A-Z]/.test(c) ? c : `A${c}`).join(',');
            const url = `https://wts-info-api.tossinvest.com/api/v1/product/stock-prices?meta=true&productCodes=${encodeURIComponent(codes)}`;
            const json = await this.fetchJson(url, {
                headers: { accept: 'application/json' },
            });
            return json.result ?? [];
        }
        async getStockPrice(productCode) {
            const list = await this.getStockPrices([productCode]);
            return list[0] ?? null;
        }
        async getTicsAll() {
            const json = await this.fetchJson('https://wts-info-api.tossinvest.com/api/v1/tics/all', { headers: { accept: 'application/json' } });
            if (!json.result)
                throw new Error('Invalid TICS all response');
            return json.result;
        }
        async getTicsDetails(id) {
            const json = await this.fetchJson(`https://wts-info-api.tossinvest.com/api/v1/tics/${id}/details`, { headers: { accept: 'application/json' } });
            if (!json.result)
                throw new Error('Invalid TICS details response');
            return json.result;
        }
        async getIndicators() {
            const json = await this.fetchJson('https://wts-cert-api.tossinvest.com/api/v4/dashboard/wts/overview/indicator', { headers: { accept: 'application/json' } });
            if (!json.result)
                throw new Error('Invalid indicator response');
            return json.result;
        }
        async getMiniCharts(codes) {
            if (!codes.length) {
                return { baseDateTime: '', baseRange: '', baseStep: '', miniCharts: [] };
            }
            const json = await this.fetchJson('https://wts-cert-api.tossinvest.com/api/v1/dashboard/common/stocks/mini-chart', {
                method: 'POST',
                headers: { accept: 'application/json', 'content-type': 'application/json' },
                body: JSON.stringify({ codes: [...codes] }),
            });
            if (!json.result)
                throw new Error('Invalid mini-chart response');
            return json.result;
        }
    };
    TossServiceImpl = __decorate([
        Sim({ symbol: TossService.SYMBOL, container })
    ], TossServiceImpl);
    return TossServiceImpl;
};
